import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import {
  Send,
  Mail,
  Users,
  AlertTriangle,
  Plus,
  Trash2,
  Loader2,
  Save,
  FileText,
  XCircle
} from 'lucide-react';

import { BACKEND_URL } from '../utils/apiClient';

const API = BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const EscalationConfig = () => {
  const [escalationRoles, setEscalationRoles] = useState([]);
  const [escalationRules, setEscalationRules] = useState([]);
  const [senderConfig, setSenderConfig] = useState({ sender_email: '', sender_name: '' });
  const [loadingEscalation, setLoadingEscalation] = useState(false);
  const [savingEscalation, setSavingEscalation] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchEscalationConfig();
  }, []);

  const fetchEscalationConfig = async () => {
    setLoadingEscalation(true);
    try {
      const [rolesRes, rulesRes, senderRes] = await Promise.all([
        axios.get(`${API}/settings/escalation/roles`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings/escalation/rules`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings/escalation/sender`, { headers: getAuthHeaders() })
      ]);
      setEscalationRoles(rolesRes.data.roles || []);
      setEscalationRules(rulesRes.data.rules || []);
      setSenderConfig({
        sender_email: senderRes.data.sender_email || senderRes.data.default_email || '',
        sender_name: senderRes.data.sender_name || senderRes.data.default_name || ''
      });
    } catch (error) {
      console.error('Error fetching escalation config:', error);
    } finally {
      setLoadingEscalation(false);
    }
  };

  // Role management
  const handleAddRole = async () => {
    if (!newRoleName.trim()) {
      toast.error('Veuillez entrer un nom de rôle');
      return;
    }
    try {
      const response = await axios.post(`${API}/settings/escalation/roles`, 
        { name: newRoleName.trim() }, 
        { headers: getAuthHeaders() }
      );
      setEscalationRoles(prev => [...prev, response.data.role]);
      setNewRoleName('');
      toast.success('Rôle créé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateRole = async (roleId, updates) => {
    try {
      await axios.put(`${API}/settings/escalation/roles/${roleId}`, updates, { headers: getAuthHeaders() });
      setEscalationRoles(prev => prev.map(r => r.id === roleId ? { ...r, ...updates } : r));
      setEditingRole(null);
      toast.success('Rôle mis à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Supprimer ce rôle ? Il sera retiré de toutes les règles d\'escalade.')) return;
    try {
      await axios.delete(`${API}/settings/escalation/roles/${roleId}`, { headers: getAuthHeaders() });
      setEscalationRoles(prev => prev.filter(r => r.id !== roleId));
      setEscalationRules(prev => prev.map(rule => ({
        ...rule,
        role_ids: rule.role_ids.filter(id => id !== roleId)
      })));
      toast.success('Rôle supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Rule management
  const handleAddRule = async () => {
    try {
      const response = await axios.post(`${API}/settings/escalation/rules`, 
        { delay_days: 1, role_ids: [], email_addresses: [], is_active: true }, 
        { headers: getAuthHeaders() }
      );
      setEscalationRules(prev => [...prev, response.data.rule].sort((a, b) => a.delay_days - b.delay_days));
      toast.success('Règle créée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateRule = async (ruleId, updates) => {
    try {
      await axios.put(`${API}/settings/escalation/rules/${ruleId}`, updates, { headers: getAuthHeaders() });
      setEscalationRules(prev => prev.map(r => r.id === ruleId ? { ...r, ...updates } : r).sort((a, b) => a.delay_days - b.delay_days));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Supprimer cette règle d\'escalade ?')) return;
    try {
      await axios.delete(`${API}/settings/escalation/rules/${ruleId}`, { headers: getAuthHeaders() });
      setEscalationRules(prev => prev.filter(r => r.id !== ruleId));
      toast.success('Règle supprimée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleToggleRoleInRule = (ruleId, roleId) => {
    const rule = escalationRules.find(r => r.id === ruleId);
    if (!rule) return;
    
    const newRoleIds = rule.role_ids.includes(roleId)
      ? rule.role_ids.filter(id => id !== roleId)
      : [...rule.role_ids, roleId];
    
    handleUpdateRule(ruleId, { role_ids: newRoleIds });
  };

  const handleAddEmailToRule = (ruleId, email) => {
    if (!email || !email.includes('@')) {
      toast.error('Veuillez entrer un email valide');
      return;
    }
    const rule = escalationRules.find(r => r.id === ruleId);
    if (!rule) return;
    
    if (rule.email_addresses.includes(email)) {
      toast.error('Cet email est déjà dans la liste');
      return;
    }
    
    handleUpdateRule(ruleId, { email_addresses: [...rule.email_addresses, email] });
  };

  const handleRemoveEmailFromRule = (ruleId, email) => {
    const rule = escalationRules.find(r => r.id === ruleId);
    if (!rule) return;
    handleUpdateRule(ruleId, { email_addresses: rule.email_addresses.filter(e => e !== email) });
  };

  const handleSaveSender = async () => {
    setSavingEscalation(true);
    try {
      await axios.put(`${API}/settings/escalation/sender`, senderConfig, { headers: getAuthHeaders() });
      toast.success('Expéditeur mis à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingEscalation(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Veuillez entrer un email valide');
      return;
    }
    setSendingTest(true);
    try {
      const response = await axios.post(`${API}/settings/escalation/sender/test`, 
        { test_email: testEmail }, 
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      setTestEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du test');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Email Sender Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            Expéditeur des notifications
          </CardTitle>
          <CardDescription>
            Les notifications seront envoyées au nom de cet expéditeur
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Email expéditeur</Label>
              <Input
                type="email"
                placeholder="admin@entreprise.com"
                value={senderConfig.sender_email}
                onChange={(e) => setSenderConfig(prev => ({ ...prev, sender_email: e.target.value }))}
                data-testid="sender-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Nom affiché</Label>
              <Input
                type="text"
                placeholder="Direction Qualité"
                value={senderConfig.sender_name}
                onChange={(e) => setSenderConfig(prev => ({ ...prev, sender_name: e.target.value }))}
                data-testid="sender-name-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Mail className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-800">
              Les emails apparaîtront comme envoyés par : <strong>{senderConfig.sender_name || 'Admin'}</strong> &lt;{senderConfig.sender_email || 'non configuré'}&gt;
            </span>
          </div>
          
          {/* Test Email Section */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <Label className="text-sm font-medium mb-2 block">Tester l'envoi d'email</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="votre@email.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleSendTestEmail()}
                data-testid="test-email-input"
              />
              <Button 
                onClick={handleSendTestEmail} 
                disabled={sendingTest || !testEmail}
                variant="outline"
                data-testid="send-test-email-btn"
              >
                {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Envoyer un test
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Un email de test sera envoyé pour vérifier la configuration
            </p>
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={handleSaveSender} 
              disabled={savingEscalation}
              data-testid="save-sender-btn"
            >
              {savingEscalation ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer l'expéditeur
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Escalation Roles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            Rôles pour les escalades
          </CardTitle>
          <CardDescription>
            Définissez les rôles qui recevront les notifications d'escalade
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingEscalation ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {escalationRoles.map((role) => (
                  <div 
                    key={role.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      role.is_system ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'
                    }`}
                  >
                    {editingRole === role.id ? (
                      <div className="flex-1 flex gap-2">
                        <Input
                          defaultValue={role.name}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateRole(role.id, { name: e.target.value });
                            } else if (e.key === 'Escape') {
                              setEditingRole(null);
                            }
                          }}
                          autoFocus
                          className="flex-1"
                        />
                        <Button size="sm" variant="ghost" onClick={() => setEditingRole(null)}>
                          Annuler
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-purple-700">{role.order}</span>
                          </div>
                          <div>
                            <p className="font-medium">{role.name}</p>
                            {role.description && (
                              <p className="text-xs text-slate-500">{role.description}</p>
                            )}
                          </div>
                          {role.is_system && (
                            <Badge variant="secondary" className="text-xs">Système</Badge>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setEditingRole(role.id)}
                            data-testid={`edit-role-${role.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          {!role.is_system && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteRole(role.id)}
                              data-testid={`delete-role-${role.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2 border-t">
                <Input
                  placeholder="Nouveau rôle (ex: Responsable HSE)"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddRole()}
                  className="flex-1"
                  data-testid="new-role-input"
                />
                <Button onClick={handleAddRole} data-testid="add-role-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Escalation Rules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            Règles d'escalade
          </CardTitle>
          <CardDescription>
            Configurez les délais et destinataires pour chaque niveau d'escalade des actions non clôturées
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingEscalation ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {escalationRules.map((rule) => (
                  <div 
                    key={rule.id}
                    className={`p-4 rounded-lg border-2 ${
                      rule.is_active 
                        ? 'border-orange-200 bg-orange-50/50' 
                        : 'border-slate-200 bg-slate-50 opacity-60'
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1 rounded-full font-bold ${
                          rule.delay_days <= 1 ? 'bg-yellow-200 text-yellow-800' :
                          rule.delay_days <= 3 ? 'bg-orange-200 text-orange-800' :
                          'bg-red-200 text-red-800'
                        }`}>
                          J+{rule.delay_days}
                        </div>
                        <Select 
                          value={String(rule.delay_days)} 
                          onValueChange={(v) => handleUpdateRule(rule.id, { delay_days: parseInt(v) })}
                        >
                          <SelectTrigger className="w-28" data-testid={`rule-delay-${rule.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 5, 7, 10, 14, 21, 30].map(d => (
                              <SelectItem key={d} value={String(d)}>J+{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={rule.is_active}
                          onCheckedChange={(v) => handleUpdateRule(rule.id, { is_active: v })}
                          data-testid={`rule-active-${rule.id}`}
                        />
                        <span className="text-sm text-slate-600">{rule.is_active ? 'Active' : 'Inactive'}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                          onClick={() => handleDeleteRule(rule.id)}
                          data-testid={`delete-rule-${rule.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Roles selection */}
                    <div className="mb-3">
                      <Label className="text-sm font-medium mb-2 block">Rôles à notifier</Label>
                      <div className="flex flex-wrap gap-2">
                        {escalationRoles.map(role => (
                          <button
                            key={role.id}
                            onClick={() => handleToggleRoleInRule(rule.id, role.id)}
                            className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                              rule.role_ids.includes(role.id)
                                ? 'bg-purple-600 text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                            data-testid={`rule-${rule.id}-role-${role.id}`}
                          >
                            {role.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Email addresses */}
                    <div className="mb-3">
                      <Label className="text-sm font-medium mb-2 block">Emails individuels</Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {rule.email_addresses.map((email, idx) => (
                          <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                            <Mail className="w-3 h-3" />
                            <span>{email}</span>
                            <button
                              onClick={() => handleRemoveEmailFromRule(rule.id, email)}
                              className="ml-1 hover:text-red-600"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="ajouter@email.com"
                          className="flex-1 h-8 text-sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value) {
                              handleAddEmailToRule(rule.id, e.target.value);
                              e.target.value = '';
                            }
                          }}
                          data-testid={`rule-${rule.id}-email-input`}
                        />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            const input = e.target.closest('div').querySelector('input');
                            if (input?.value) {
                              handleAddEmailToRule(rule.id, input.value);
                              input.value = '';
                            }
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Options */}
                    <div className="flex flex-wrap gap-4 pt-3 border-t border-slate-200">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={rule.include_action_creator}
                          onChange={(e) => handleUpdateRule(rule.id, { include_action_creator: e.target.checked })}
                          className="rounded"
                        />
                        Inclure le créateur de l'action
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={rule.include_zone_owner}
                          onChange={(e) => handleUpdateRule(rule.id, { include_zone_owner: e.target.checked })}
                          className="rounded"
                        />
                        Inclure le responsable de zone
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                onClick={handleAddRule} 
                variant="outline" 
                className="w-full border-dashed"
                data-testid="add-rule-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une règle d'escalade
              </Button>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mt-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Comment fonctionnent les escalades ?</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Les notifications sont envoyées automatiquement pour les actions non clôturées après le délai spécifié (J+N après la date d'échéance).
                      Si l'action est clôturée avant, les escalades suivantes sont annulées.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EscalationConfig;
